import businessTypesData from '@/assets/business-types.json';
import { useBusinessTypes } from '@/hooks/useBusinessTypes';
import { useLeadCounts } from '@/hooks/useLeadCounts';
import {
  addBusinessTypeRequests,
  businessTypeExists,
  createBusinessType,
  createList,
  createSlug,
  deleteBusinessType,
  getSequencesList,
  updateBusinessTypeDefaultSequence,
  updateBusinessTypeMetadata,
  type ApolloSequence,
  type BusinessType,
  type BusinessTypeRequest,
} from '@/services/firebase/firestore';
import { AddLeadModal } from '@components/AddLeadModal';
import { AddListModal } from '@components/AddListModal';
import { AddNewTypeModal } from '@components/AddNewTypeModal';
import { Button } from '@components/Button';
import { EditMetadataModal } from '@components/EditMetadataModal';
import { EmptyState } from '@components/EmptyState';
import { GenerateMoreModal, type CityEntry } from '@components/GenerateMoreModal';
import { Header } from '@components/Header';
import {
  IconFilter,
  IconLightning,
  IconLoading,
  SearchListIcon,
  SelectFromListIcon,
} from '@components/icons';
import { IconButton } from '@components/IconButton';
import { Input } from '@components/Input';
import { LeadTypeViewHeader } from '@components/LeadTypeViewHeader';
import { ModalLayout } from '@components/ModalLayout';
import { PageHeader } from '@components/PageHeader';
import { SelectableRow } from '@components/SelectableRow';
import { TableHeaderCell } from '@components/TableHeaderCell';
import { useAuthStore } from '@store/index';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AddMenu } from './AddMenu';
import styles from './LeadsPage.module.scss';

export interface LeadsPageProps {
  /** Initial list of types (empty for initial state demo) */
  initialTypes?: BusinessType[];

  /** Logout handler */
  onLogout?: () => void;
}

export const LeadsPage = ({ initialTypes = [], onLogout: onLogoutProp }: LeadsPageProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signOut } = useAuthStore();
  const {
    businessTypes,
    loading,
    error,
    selectedBusinessType,
    selectedBusinessTypeLoading,
    setSelectedBusinessType,
  } = useBusinessTypes();
  const demoState = searchParams.get('state');

  // Capture deep-link type param once on mount (survives URL cleanup)
  const [deepLinkType] = useState(() => searchParams.get('type'));
  const deepLinkConsumedRef = useRef(false);

  // Auto-select business type from URL query param (deep link from Manage Leads)
  useEffect(() => {
    if (!deepLinkType || deepLinkConsumedRef.current || loading || businessTypes.length === 0)
      return;
    const match = businessTypes.find((bt) => bt.slug === deepLinkType);
    if (match) {
      deepLinkConsumedRef.current = true;
      setSelectedBusinessType(match);
      navigate('/leads', { replace: true });
    }
  }, [deepLinkType, loading, businessTypes, setSelectedBusinessType, navigate]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
    onLogoutProp?.();
  };

  const { t } = useTranslation();

  // State for UI interactions
  const [filterValue, setFilterValue] = useState('');
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const filterBlurTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const filterInputRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);
  const [isAddListModalOpen, setIsAddListModalOpen] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);

  // Use real data from Firestore, but allow demo states to override for demos
  const getDisplayData = () => {
    if (demoState === 'initial') {
      return { types: [] as BusinessType[], selectedId: null };
    }
    if (demoState === 'populated' && initialTypes.length > 0) {
      return { types: initialTypes, selectedId: null };
    }
    if (demoState === 'selected' && initialTypes.length > 0) {
      return { types: initialTypes, selectedId: initialTypes[0]?.id ?? null };
    }
    // Use real Firestore data
    return {
      types: businessTypes,
      selectedId: selectedBusinessType?.id ?? null,
    };
  };

  const displayData = getDisplayData();
  const types = displayData.types;
  const selectedTypeId = displayData.selectedId;
  const [isGenerateMoreOpen, setIsGenerateMoreOpen] = useState(false);
  const [isEditMetadataOpen, setIsEditMetadataOpen] = useState(false);
  const [availableSequences, setAvailableSequences] = useState<ApolloSequence[]>([]);

  // Fetch available Apollo sequences
  useEffect(() => {
    getSequencesList()
      .then((seqs) => setAvailableSequences([...seqs].sort((a, b) => a.name.localeCompare(b.name))))
      .catch((error) => console.error('Failed to load sequences:', error));
  }, []);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const filteredTypes = types.filter((type: BusinessType) =>
    type.name.toLowerCase().includes(filterValue.toLowerCase())
  );

  // Live per-type lead counts via Firestore count() aggregation
  const { counts: leadCounts, refresh: refreshLeadCounts } = useLeadCounts(
    types.map((t) => t.slug)
  );

  // Clean up blur timeout on unmount
  useEffect(() => () => clearTimeout(filterBlurTimeoutRef.current), []);

  // Focus the filter input when expanded
  useEffect(() => {
    if (isFilterExpanded) {
      const input = filterInputRef.current?.querySelector('input');
      input?.focus();
    }
  }, [isFilterExpanded]);

  const handleFilterBlur = () => {
    filterBlurTimeoutRef.current = setTimeout(() => {
      setIsFilterExpanded(false);
    }, 150);
  };

  const handleFilterClear = () => {
    clearTimeout(filterBlurTimeoutRef.current);
    setFilterValue('');
    setIsFilterExpanded(false);
  };

  const handleFilterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsFilterExpanded(false);
    }
  };

  const selectedType =
    selectedBusinessType || types.find((t: BusinessType) => t.id === selectedTypeId);

  // Handle type selection
  const handleTypeSelect = (type: BusinessType) => {
    if (!demoState || demoState === 'populated') {
      // In real mode, use the hook's setter
      setSelectedBusinessType(type);
    }
  };

  const handleSaveType = async (typeName: string, onExist: () => void, onCreated: () => void) => {
    try {
      const slug = createSlug(typeName);

      // Check if business type already exists
      const exists = await businessTypeExists(slug);

      if (exists) {
        // Business type already exists
        onExist();
        return;
      }

      // Create the business type with just name and slug
      await createBusinessType({
        name: typeName,
        slug: slug,
      });

      // Notify modal that it was created
      onCreated();
    } catch (error) {
      console.error('Failed to create business type:', error);
      // TODO: Show error toast/notification
    }
  };

  const handleDiscardType = async (typeName: string) => {
    try {
      const slug = createSlug(typeName);
      await deleteBusinessType(slug);
    } catch (error) {
      console.error('Failed to delete business type:', error);
    }
  };

  const handleSequenceChange = async (sequenceId: string | null) => {
    if (!selectedType) return;
    try {
      await updateBusinessTypeDefaultSequence(selectedType.slug, sequenceId);
    } catch (error) {
      console.error('Failed to update default sequence:', error);
    }
  };

  const handleDeleteType = async () => {
    if (!selectedType) return;
    try {
      await deleteBusinessType(selectedType.slug);
      setIsDeleteConfirmOpen(false);
    } catch (error) {
      console.error('Failed to delete business type:', error);
    }
  };

  const handleOpenExistingType = (typeName: string) => {
    // Find the business type by name and select it
    const existingType = types.find((type) => type.name === typeName);
    if (existingType) {
      setSelectedBusinessType(existingType);
    }
  };

  const handleMetadataUpdate = async (
    typeName: string,
    metadata: { website_objectives: string[]; industry_painpoints: string[] }
  ) => {
    try {
      const slug = createSlug(typeName);
      await updateBusinessTypeMetadata(slug, metadata);
    } catch (error) {
      console.error('Failed to update business type metadata:', error);
      // TODO: Show error toast/notification
    }
  };

  // Format Firestore timestamp to readable string
  const formatTimestamp = (timestamp: { toDate?: () => Date } | null | undefined): string => {
    if (!timestamp) return 'No date available';

    try {
      const date = timestamp.toDate?.();
      if (!date) return 'No date available';
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Invalid date';
    }
  };

  const hasTypes = types.length > 0;

  return (
    <>
      <div className={styles.page}>
        {/* Header with navigation */}
        <Header
          showLogout={true}
          showTabs={true}
          tabs={[
            { label: 'Overview', active: false, onClick: () => navigate('/') },
            { label: 'Generate Leads', active: true },
            { label: 'Manage Leads', active: false, onClick: () => navigate('/manage-leads') },
          ]}
          onLogout={handleLogout}
        />

        {/* Main content area */}
        <main className={styles.main}>
          <div className={styles.wrapper}>
            {/* Page header */}
            <PageHeader
              title="Generate Leads"
              actionSlot={
                !loading || !!demoState ? (
                  <AddMenu
                    isOpen={isAddMenuOpen}
                    onOpenChange={setIsAddMenuOpen}
                    onAddType={() => setIsModalOpen(true)}
                    onAddLead={() => setIsAddLeadModalOpen(true)}
                    onAddList={() => setIsAddListModalOpen(true)}
                  />
                ) : undefined
              }
            />

            {/* Content */}
            {loading && !demoState ? (
              // Loading state for Firestore data
              <div className={styles.card}>
                <EmptyState
                  icon={<SearchListIcon />}
                  title="Loading business types..."
                  subtitle="Please wait while we fetch your data"
                />
              </div>
            ) : error && !demoState ? (
              // Error state for Firestore data
              <div className={styles.card}>
                <EmptyState
                  icon={<SearchListIcon />}
                  title="Failed to load business types"
                  subtitle={`Error: ${error}`}
                  primaryActionText="Retry"
                  onPrimaryAction={() => window.location.reload()}
                />
              </div>
            ) : !hasTypes ? (
              // Initial empty state
              <div className={styles.card}>
                <EmptyState
                  icon={<SearchListIcon width={82} height={62} />}
                  title="No types added yet"
                  subtitle="Start by adding your first type to generate and manage your leads"
                  primaryActionText="Add New Type"
                  onPrimaryAction={() => setIsModalOpen(true)}
                />
              </div>
            ) : (
              // Two-column layout with types
              <div className={styles.content}>
                {/* Left sidebar: Type list */}
                <div className={styles.typeList}>
                  <div className={styles.filterRow} ref={filterInputRef}>
                    {isFilterExpanded ? (
                      <Input
                        value={filterValue}
                        onChange={setFilterValue}
                        placeholder={t('common.filter')}
                        leadingIcon={<IconFilter size={18} />}
                        clearable
                        onBlur={handleFilterBlur}
                        onClear={handleFilterClear}
                        onKeyDown={handleFilterKeyDown}
                      />
                    ) : (
                      <>
                        <span className={`${styles.countLabel}${filterValue ? ` ${styles.countFiltered}` : ''}`}>
                          {filterValue
                            ? t('generateLeads.typeCountFiltered', { filtered: filteredTypes.length, total: types.length, count: types.length })
                            : t('generateLeads.typeCount', { count: filteredTypes.length })}
                        </span>
                        <IconButton
                          size="small"
                          aria-label={t('common.filter')}
                          onClick={() => setIsFilterExpanded(true)}
                          className={filterValue ? styles.filterActive : undefined}
                        >
                          <IconFilter size={14} />
                        </IconButton>
                      </>
                    )}
                  </div>
                  <div className={styles.typeListHeader}>
                    <TableHeaderCell title="Name" className={styles.typeListHeaderName} />
                    <TableHeaderCell title="Output" className={styles.typeListHeaderOutput} />
                  </div>
                  <div className={styles.typeListItems}>
                    {filteredTypes.length === 0 && filterValue ? (
                      <div className={styles.noResultsState}>
                        <EmptyState
                          size="minimal"
                          icon={<IconFilter size={40} />}
                          title="No results found"
                          subtitle="Try a different search term"
                        />
                      </div>
                    ) : (
                      filteredTypes.map((type: BusinessType) => (
                        <SelectableRow
                          key={type.id}
                          label={type.name}
                          value={leadCounts[type.slug] ?? type.output ?? 0}
                          selected={selectedTypeId === type.id}
                          onClick={() => handleTypeSelect(type)}
                        />
                      ))
                    )}
                  </div>
                </div>

                {/* Right content area */}
                <div className={styles.rightContent}>
                  {!selectedType ? (
                    // No type selected
                    <div className={styles.emptySelection}>
                      <EmptyState
                        icon={<SelectFromListIcon width={82} height={62} />}
                        title="No Type Selected"
                        subtitle="Select a type from the list to view details"
                      />
                    </div>
                  ) : (
                    // Type selected - show details
                    <>
                      <div className={styles.rightContentHeader}>
                        <LeadTypeViewHeader
                          title={selectedType.name}
                          slug={selectedType.slug}
                          date={
                            selectedType.ts?.toDate
                              ? `Updated ${selectedType.ts.toDate().toLocaleDateString()} ${selectedType.ts.toDate().toLocaleTimeString()}`
                              : 'No date available'
                          }
                          totalLeads={leadCounts[selectedType.slug] ?? selectedType.output ?? 0}
                          onTotalLeadsClick={() =>
                            navigate(`/manage-leads?businessType=${selectedType.slug}`)
                          }
                          websiteObjectives={selectedType.metadata?.website_objectives || []}
                          industryPainpoints={selectedType.metadata?.industry_painpoints || []}
                          showEditButton
                          onEdit={() => setIsEditMetadataOpen(true)}
                          sequenceId={selectedType.default_sequence_id}
                          sequenceName={
                            availableSequences.find(
                              (s) => s.sequence_id === selectedType.default_sequence_id
                            )?.name
                          }
                          availableSequences={availableSequences}
                          onSequenceChange={handleSequenceChange}
                          showDeleteButton
                          onDelete={() => setIsDeleteConfirmOpen(true)}
                        />
                      </div>

                      {/* History Section */}
                      <div className={styles.historySection}>
                        <div className={styles.historySectionHeader}>
                          <h2 className={styles.historySectionTitle}>History</h2>
                          <Button
                            variant="primary"
                            size="small"
                            leadingIcon={
                              selectedType.requests && selectedType.requests.length > 0 ? (
                                <IconLoading size={18} />
                              ) : (
                                <IconLightning size={18} />
                              )
                            }
                            onClick={() => setIsGenerateMoreOpen(true)}
                            disabled={selectedType.requests && selectedType.requests.length > 0}
                          >
                            Generate More
                          </Button>
                        </div>

                        {selectedBusinessTypeLoading
                          ? null
                          : (() => {
                              // Combine history and requests into a single array
                              const historyEntries =
                                selectedType.history?.map((item) => ({
                                  ...item,
                                  source: 'history' as const,
                                })) || [];
                              const requestEntries =
                                selectedType.requests?.map((item) => ({
                                  ...item,
                                  output: 0, // Always 0 to show loader
                                  source: 'requests' as const,
                                  ts: undefined, // Requests don't have timestamps
                                })) || [];
                              const allEntries = [...requestEntries, ...historyEntries];

                              return allEntries.length > 0 ? (
                                <div className={styles.historyTable}>
                                  <div className={styles.historyTableHeader}>
                                    <div
                                      className={`${styles.historyTableCell} ${styles.historyTableCellId}`}
                                    >
                                      ID
                                    </div>
                                    <div
                                      className={`${styles.historyTableCell} ${styles.historyTableCellName}`}
                                    >
                                      Name
                                    </div>
                                    <div
                                      className={`${styles.historyTableCell} ${styles.historyTableCellType}`}
                                    >
                                      Type
                                    </div>
                                    <div
                                      className={`${styles.historyTableCell} ${styles.historyTableCellRequest}`}
                                    >
                                      Request
                                    </div>
                                    <div
                                      className={`${styles.historyTableCell} ${styles.historyTableCellOutput}`}
                                    >
                                      Output
                                    </div>
                                    <div
                                      className={`${styles.historyTableCell} ${styles.historyTableCellCreated}`}
                                    >
                                      Created
                                    </div>
                                  </div>

                                  <div className={styles.historyTableBody}>
                                    {allEntries.map((item, index) => (
                                      <div
                                        key={`${item.id}-${index}`}
                                        className={styles.historyTableRow}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => {
                                          const params = new URLSearchParams();
                                          params.set('businessType', selectedType.slug);
                                          params.set('cityId', String(item.id));
                                          params.set('cityName', item.name);
                                          navigate(`/manage-leads?${params.toString()}`);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            const params = new URLSearchParams();
                                            params.set('businessType', selectedType.slug);
                                            params.set('cityId', String(item.id));
                                            params.set('cityName', item.name);
                                            navigate(`/manage-leads?${params.toString()}`);
                                          }
                                        }}
                                      >
                                        <div
                                          className={`${styles.historyTableCell} ${styles.historyTableCellId}`}
                                        >
                                          {item.id}
                                        </div>
                                        <div
                                          className={`${styles.historyTableCell} ${styles.historyTableCellName}`}
                                        >
                                          {item.name}
                                        </div>
                                        <div
                                          className={`${styles.historyTableCell} ${styles.historyTableCellType}`}
                                        >
                                          {item.type}
                                        </div>
                                        <div
                                          className={`${styles.historyTableCell} ${styles.historyTableCellRequest}`}
                                        >
                                          {item.request}
                                        </div>
                                        <div
                                          className={`${styles.historyTableCell} ${styles.historyTableCellOutput}`}
                                        >
                                          {item.source === 'requests' ? (
                                            <IconLoading size={14} />
                                          ) : (
                                            item.output
                                          )}
                                        </div>
                                        <div
                                          className={`${styles.historyTableCell} ${styles.historyTableCellCreated}`}
                                        >
                                          {item.source === 'requests'
                                            ? new Date().toLocaleDateString() +
                                              ' ' +
                                              new Date().toLocaleTimeString()
                                            : formatTimestamp(item.ts)}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className={styles.emptyHistory}>
                                  <EmptyState
                                    icon={<SearchListIcon />}
                                    title="No History Available"
                                    subtitle="No lead generation history found for this business type"
                                  />
                                </div>
                              );
                            })()}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={isAddLeadModalOpen}
        onClose={() => setIsAddLeadModalOpen(false)}
        onAdded={() => refreshLeadCounts()}
      />

      {/* Add List Modal (SC-2193) */}
      <AddListModal
        isOpen={isAddListModalOpen}
        onClose={() => setIsAddListModalOpen(false)}
        onSave={(id) => createList(id)}
      />

      {/* Add New Type Modal */}
      <AddNewTypeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveType}
        onMetadataUpdate={handleMetadataUpdate}
        onDiscard={handleDiscardType}
        onOpenExisting={handleOpenExistingType}
        availableTypes={businessTypesData}
        existingTypes={types.map((t) => t.name)}
      />

      {/* Generate More Modal */}
      {selectedType && (
        <GenerateMoreModal
          isOpen={isGenerateMoreOpen}
          onClose={() => setIsGenerateMoreOpen(false)}
          typeName={selectedType.name}
          onGenerate={async (cities: CityEntry[]) => {
            try {
              // Convert cities to BusinessTypeRequest format
              const requests: BusinessTypeRequest[] = cities.map((city) => ({
                name: `${city.name} (${city.country})`,
                id: city.id,
                request: city.numLeads,
                type: city.pipelineType ?? 'gemini',
              }));

              // Save requests to Firebase
              await addBusinessTypeRequests(selectedType.slug, requests);
            } catch (error) {
              console.error('Error saving requests:', error);
              throw error; // Re-throw to let modal handle the error
            }
          }}
        />
      )}

      {/* Edit Metadata Modal */}
      {selectedType && (
        <EditMetadataModal
          isOpen={isEditMetadataOpen}
          onClose={() => setIsEditMetadataOpen(false)}
          typeName={selectedType.name}
          websiteObjectives={selectedType.metadata?.website_objectives || []}
          industryPainpoints={selectedType.metadata?.industry_painpoints || []}
          defaultApolloSequence={selectedType.default_sequence_id}
          availableSequences={availableSequences}
          onSave={async (data) => {
            try {
              await Promise.all([
                updateBusinessTypeMetadata(selectedType.slug, {
                  website_objectives: data.websiteObjectives,
                  industry_painpoints: data.industryPainpoints,
                }),
                updateBusinessTypeDefaultSequence(selectedType.slug, data.apolloSequence),
              ]);
            } catch (error) {
              console.error('Error updating metadata:', error);
            }
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteConfirmOpen && selectedType && (
        <div
          className={styles.deleteBackdrop}
          role="presentation"
          onClick={() => setIsDeleteConfirmOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setIsDeleteConfirmOpen(false)}
        >
          <div
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <ModalLayout
              title="Delete Business Type"
              onDismiss={() => setIsDeleteConfirmOpen(false)}
              primaryActionText="Delete"
              secondaryActionText="Cancel"
              onPrimaryAction={handleDeleteType}
              onSecondaryAction={() => setIsDeleteConfirmOpen(false)}
              width={480}
            >
              <div className={styles.deleteModalContent}>
                <p className={styles.deleteModalText}>
                  Are you sure you want to delete{' '}
                  <strong className={styles.deleteModalTypeName}>{selectedType.name}</strong>?
                </p>
                <p className={styles.deleteModalWarning}>
                  This action cannot be undone. All generated leads and history for this type will
                  be permanently removed.
                </p>
              </div>
            </ModalLayout>
          </div>
        </div>
      )}
    </>
  );
};
